import type { HouseMap } from "@/types/entity";
import api from "@/modules/api";
import { defineStore } from "pinia";
import { useGlobalUser } from "@/stores/globalUser";

const PER_PAGE = 999;

export const useHouseMapStore = defineStore("houseMap", {
  state: () => {
    return {
      houseMapIds: [] as number[],
      entityMap: {} as Record<number, HouseMap>,
      pageNo: 1,
    };
  },
  getters: {
    houseMapList: (state): HouseMap[] => {
      return state.houseMapIds.map((id) => state.entityMap[id]!);
    },
    uuid2EntityMap: (state): Record<string, HouseMap> => {
      return Object.values(state.entityMap).reduce((acc, item) => {
        acc[item.uuid] = item;
        return acc;
      }, {} as Record<string, HouseMap>);
    },
  },
  actions: {
    async fetchHouseMapPage(
      param: Parameters<typeof api.readHouseMapPage>[0]
    ): Promise<HouseMap[]> {
      const res = await api.readHouseMapPage(
        Object.assign({ sort: { order: "desc", field: "dtUpdate" } }, param)
      );
      this.updateEntityMap(res.page.data);
      this.pageNo++;
      return res.page.data;
    },
    async fetchNextPage(): Promise<boolean> {
      const data = await this.fetchHouseMapPage({
        pagination: { page: this.pageNo, perPage: PER_PAGE },
      });
      this.houseMapIds.push(...data.map((item) => item.id!));
      return data.length === PER_PAGE;
    },
    updateEntityMap(houseMap: HouseMap | HouseMap[]) {
      const list = Array.isArray(houseMap) ? houseMap : [houseMap];
      list.forEach((item) => {
        const old = this.entityMap[item.id!];
        if (old) {
          Object.assign(old, item);
        } else {
          this.entityMap[item.id!] = item;
        }
      });
    },
    async createHouseMap(
      params: Parameters<typeof api.createHouseMap>[0]
    ): Promise<HouseMap> {
      const res = await api.createHouseMap(params);
      this.updateEntityMap(res.item);
      return res.item;
    },
    async updateHouseMap(
      id: number,
      params: Parameters<typeof api.updateHouseMap>[1]
    ): Promise<HouseMap> {
      const res = await api.updateHouseMap(id, params);
      this.updateEntityMap(res.item);
      return res.item;
    },
    async deleteHouseMap(id: number): Promise<void> {
      await api.deleteHouseMap(id);
      this.houseMapIds = this.houseMapIds.filter((item) => item !== id);
    },
    async getOrFetchHouseMap(
      uuid: string,
      force = false
    ): Promise<HouseMap | null> {
      if (!force && this.uuid2EntityMap[uuid]) {
        return this.uuid2EntityMap[uuid];
      } else {
        const res = await api.readOneHouseMap(uuid);
        this.updateEntityMap(res.item);
        return res.item;
      }
    },
  },
});
